"use client";

import styles from "./NsatBanner.module.scss";
import formStyles from "./NsatRegistrationForm/NsatRegistrationForm.module.scss";
import NsatRegistrationForm from "./NsatRegistrationForm/NsatRegistrationForm";
import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  OTPContext,
  OTPContextProvider,
  OTPContextWrapper,
} from "@/components/sections/CommonModals/context";
import TickMarkBlueColor from "@/SvgIcons/TickMarkBlueColor";
import { useModalDispatch } from "@/context/ModalContext";
import { IModalActions, ModalTypes } from "@/types/modal";
import ApplicationFormRedirectLoader from "@/components/molecules/ApplicationFormRedirectLoader/ApplicationFormRedirectLoader";
import clsx from "clsx";
import { IS_AONSAT_ENABLED } from "@/context/utils";
import Badge from "@/SvgIcons/Badge";
import Cup from "@/SvgIcons/Cup";

const ClipboardIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
    <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
  </svg>
);

const MonitorIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="2" y="3" width="20" height="14" rx="2" />
    <line x1="8" y1="21" x2="16" y2="21" />
    <line x1="12" y1="17" x2="12" y2="21" />
  </svg>
);

const CalendarIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const RupeeIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M6 3h12" />
    <path d="M6 7h12" />
    <path d="M6 11h6a4 4 0 0 0 0-8" />
    <path d="M6 11l8 10" />
  </svg>
);

const ArrowCircleIcon = () => (
  <span
    className={styles.arrowCircleSpan}
    style={{
      borderRadius: "50%",
      background: "#ffffff",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#1A325D" // blue color
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="18" cy="5" r="3" />
      <circle cx="6" cy="12" r="3" />
      <circle cx="18" cy="19" r="3" />
      <line x1="8.7" y1="10.7" x2="15.3" y2="6.3" />
      <line x1="8.7" y1="13.3" x2="15.3" y2="17.7" />
    </svg>
  </span>
);

const INFO_ITEMS = [
  { Icon: ClipboardIcon, label: "Registrations:", value: "Live Now!" },
  { Icon: MonitorIcon, label: "Mode:", value: "Online Proctored Test" },
  { Icon: CalendarIcon, label: "Test Date:", value: "June 7, 2026" },
  { Icon: RupeeIcon, label: "Registration Fee:", value: "FREE" },
];

interface NsatBannerProps {
  bgImage?: string;
  associatedPrograms?: Array<ProgramDetails>;
  isLambdaAPI?: boolean;
  metaApiParams?: any;
  submitBtnText?: string;
  rfiCase?: string;
}

export default function NsatBanner({
  bgImage,
  associatedPrograms = [],
  isLambdaAPI = false,
  metaApiParams,
  submitBtnText,
  rfiCase = "REDIRECT_TO_APPLICATION_JOURNEY",
}: NsatBannerProps) {
  const [slotDate, setSlotDate] = useState("")
  const modalDispatch = useModalDispatch();
  const [redirectTime, setRedirectTime] = useState(3);
  const [isOtpValidated, setIsOtpValidated] = useState(false);
  const [leadFailed, setLeadFailed] = useState(false);
  const testUrl = useRef(null);

  const [isApplicationRedirectLoaderDisabled, setApplicationRedirectLoaderDisabled] =
    useState(false);


  const isLeadFailedMethod = (status: boolean) => {
    setLeadFailed(status);
  };

  const handleOtpValidation = useCallback((status) => {
    setIsOtpValidated(status);
    setApplicationRedirectLoaderDisabled(true)
  }, []);


  useEffect(() => {
      if (isApplicationRedirectLoaderDisabled) {
      rfiFunctionality();
    }
  }, [isApplicationRedirectLoaderDisabled]);


  const handleShare = () => {
    modalDispatch({
      type: IModalActions.OpenModal,
      data: {
        modalType: ModalTypes.ShareIcon,
        modalData: { isNsat: true },
      },
    });
  };

  const redirectThankyou = () => {
    const timer = setInterval(() => {
      setRedirectTime((prevTime) => {
        if (prevTime > 1) {
          return prevTime - 1;
        } else {
          clearInterval(timer);

          window.location.href = "/thankyou-aonsat";

        }
        return prevTime;
      });
    }, 1000);
  };

  const redirectAppplicationJourney = () => {
    const timer = setInterval(() => {
      setRedirectTime((prevTime) => {
        if (prevTime > 1) {
          return prevTime - 1;
        } else {
          clearInterval(timer);

          if(testUrl.current){
             window.location.href = testUrl.current;
          }else{
             window.location.href = "/thankyou-aonsat";
          }

        }
        return prevTime;
      });
    }, 1000);
  };

    const rfiFunctionality = () => {
      switch (rfiCase) {
        case "NORMAL":
          break;

        case "REDIRECT_TO_THANKYOU":
          redirectThankyou();
          break;

        case "REDIRECT_TO_APPLICATION_JOURNEY":
          redirectAppplicationJourney();
          break;

        default:
          return null;
      }
    };
  

  const checkRFICase = {
    NORMAL: (
      <>
        <div className={styles.svgContainer}>
          <TickMarkBlueColor />
        </div>
        <div className={styles.successTitle}>You&apos;re Successfully Registered!</div>
        <p className={styles.successDesc}>
          You have successfully secured your slot for NSAT 2026. Please check your email and SMS for
          the test instructions and credentials.
        </p>
        <div className={styles.testDate}>
          <div className={styles.testDateInner}>
            Test Date: <span className="lining-nums">7</span>th June |{" "}
            <span className="lining-nums">{slotDate}</span>
          </div>
        </div>
      </>
    ),
    REDIRECT_TO_THANKYOU: (
      <>
        <ApplicationFormRedirectLoader text="" redirectTime={redirectTime} />
      </>
    ),
    REDIRECT_TO_APPLICATION_JOURNEY: (
      <>
        <ApplicationFormRedirectLoader text="" redirectTime={redirectTime} />
      </>
    ),
  };

  return (
    <section
      className={styles.section}
      style={
        bgImage
          ? { backgroundImage: `linear-gradient(#0026624D, #0026624D), url(${bgImage})` }
          : undefined
      }
    >
      <div className={styles.card}>
        <div className={styles.content}>
          <p className={styles.brandName}>AMITY ONLINE</p>
          <h1 className={styles.title}>
            NSAT <span className={clsx(styles.titleAccent, "lining-nums")}>2026</span>
          </h1>
          <h2 className={styles.subtitle}>
            YOUR <span className={styles.subtitleAccent}>RANK.</span> YOUR{" "}
            <span className={styles.subtitleAccent}>REWARD.</span>
          </h2>
          <p className={styles.description}>
            Participate in The {/* <br /> */}
            {/* Desktop: National Scholarship Aptitude Test */}
            <span className={styles.descDesktop}>
              <span className={styles.firstLetter}>N</span>ational{" "}
              <span className={styles.firstLetter}>S</span>cholarship{" "}
              <span className={styles.firstLetter}>A</span>ptitude{" "}
              <span className={styles.firstLetter}>T</span>est
            </span>
            {/* Mobile: Mega Scholarship Admission Test */}
            <span className={styles.descMobile}>
              <span className={styles.firstLetter}>N</span>ational{" "}
              <span className={styles.firstLetter}>S</span>cholarship{" "}
              <span className={styles.firstLetter}>A</span>ptitude{" "}
              <span className={styles.firstLetter}>T</span>est
            </span>{" "}
            and Get
            <br /> Upto{" "}
            <span className={clsx(styles.firstLetter, "lining-nums")}>100% Scholarship</span> on
            your UG / PG degree.{" "}
            <span onClick={handleShare} className={styles.descIcon}>
              <ArrowCircleIcon />
            </span>
          </p>
          {/* <p className={styles.brandName}>
            AMITY ONLINE{" "}
            <span className={styles.titleAccent}>
              NSAT <span className="lining-nums">2026</span>
            </span>
          </p>
          <p className={styles.description}>Congratulations to the top Rankers For</p>
          <p className={styles.description}>securing Exclusive Scholarships</p>

          <p className={styles.mwebSecure}>Congratulations to the top Rankers For securing Exclusive Scholarships</p> */}


          {/* <div>
            <div className={styles.runnerUp}>
              <div className={styles.perRunnerUP}>
                <div className={styles.imgWrap}>
                  <img
                    src="https://static.amityonline.com/_s/09a1fd56e029e85ea0e2dae778c189356c1c0adf_3eb917b164.png"
                    alt="First Runner up"
                    className={styles.photo}
                  />
                  <div className={styles.cupOuter}><div className={styles.cup}><Cup /></div></div>
                </div>
                <div>
                  <div className={styles.candidateName}>Raushan <br/> Kumar</div>
                  <div className={styles.program}>UG Program</div>
                  <div className={styles.scholorship}>
                    {" "}
                    <Badge /> <span>100% <br/>SCHOLARSHIP</span>
                  </div>
                </div>
              </div>
               <div className={styles.perRunnerUP}>
                <div className={styles.imgWrap}>
                  <img
                     src="https://static.amityonline.com/_s/d70bb9553233b2f515d2ed8e03f1b0f7c24b6a6b_a1ae7ef455.jpg"
                    alt="First Runner up"
                    className={styles.photo}
                  />
                  <div className={styles.cupOuter}><div className={styles.cup}><Cup /></div></div>
                </div>
                <div>
                  <div className={styles.candidateName}>Abhishek <br/> Kushwaha</div>
                  <div className={styles.program}>PG Program</div>
                  <div className={styles.scholorship}>
                    {" "}
                    <Badge /> <span>100%<br/> SCHOLARSHIP</span>
                  </div>
                </div>
              </div>
            </div>
          </div> */}

          {!IS_AONSAT_ENABLED ? (
            <div className={styles.registerationsWrap}>
              <span className={styles.registrationsClosed}>
                Registrations for Amity Online NSAT <span className="lining-nums">2026</span> June
                edition are now closed.
              </span>
              <br />
              <span className={styles.registrationsClosed}>
                We’ll be back soon with more scholarship opportunities.
              </span>
            </div>
          ) : (
            <div className={styles.infoBox}>
              {INFO_ITEMS.map(({ Icon, label, value }, i) => (
                <div key={i} className={styles.infoItem}>
                  <span className={styles.infoIcon}>
                    <Icon />
                  </span>
                  <span className={styles.infoText}>
                    {label}&nbsp;
                    <span className={`${styles.infoValue} lining-nums`}>{value}</span>
                  </span>
                </div>
              ))}
            </div>
          )}
           <p className={styles.disclaimer}>
            *Scholarships available for a limited period. The scholarship does not apply to <br />{" "}
            students who are already enrolled with amity online
          </p>
        </div>

        <div className={styles.formWrapper}>
          {isApplicationRedirectLoaderDisabled ? (
            <div className={styles.successWrapper}>{checkRFICase[rfiCase]}</div>
          ) : (
            <div
              className={formStyles.formCard}
              style={!IS_AONSAT_ENABLED ? { background: "#f5f5f5" } : {}}
            >
              <OTPContextProvider isLambdaAPI={isLambdaAPI}>
                <OTPContext.Consumer>
                  {({ leadResponse }) => {
                    const isExistingUser = leadResponse?.ID == "0";
                    return (
                      <>
                        {!isOtpValidated && !isExistingUser && !leadFailed && (
                          <h3 className={formStyles.formHeading}>
                           {IS_AONSAT_ENABLED ? <>Register for Amity Online NSAT {" "}
                            <span className="lining-nums">2026</span>
                            </> : "Enquire Now for Upcoming Amity Online NSAT Opportunities"} 
                          </h3>
                        )}
                        <OTPContextWrapper isNewProgramTemp={true}>
                          <>
                            {!isOtpValidated && !isExistingUser && !leadFailed && (
                              <NsatRegistrationForm
                                isOtpValidated={handleOtpValidation}
                                associatedPrograms={associatedPrograms}
                                setLeadFailed={isLeadFailedMethod}
                                isLambdaAPI={isLambdaAPI}
                                metaApiParams={metaApiParams || {}}
                                submitBtnText={submitBtnText}
                                isFormDisabed={false}
                                testUrl={testUrl}
                                setSlotDate={setSlotDate}
                              />
                            )}
                          </>
                        </OTPContextWrapper>
                      </>
                    );
                  }}
                </OTPContext.Consumer>
              </OTPContextProvider>
            </div>
          )}
        </div>

        <p className={styles.disclaimermob}>
          *Scholarships available for a limited period. The scholarship does not apply to students
          who are already enrolled with amity online
        </p>
      </div>
    </section>
  );
}
